import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Container, Row, Col, Form, Spinner, Button } from "react-bootstrap";
import { Title } from "../components/common/Title";
import EpicRunsByHourChart from "../components/charts/EpicRunsByHourChart";
import EpicRunsTable from "../components/epics/EpicRunsTable";
import { EpicRun } from "../types/EpicRun";
import { getEpicColor } from "../utils/epicColors";
import {
  useComponentTracking,
  useTimeTracking,
  analytics
} from "../utils/analytics";

type RangeOption = "30" | "90" | "year" | "all";

const rangeToStartDate = (range: RangeOption): string | undefined => {
  const now = new Date();
  switch (range) {
    case "30": {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return d.toISOString();
    }
    case "90": {
      const d = new Date(now);
      d.setDate(d.getDate() - 90);
      return d.toISOString();
    }
    case "year":
      return new Date(now.getFullYear(), 0, 1).toISOString();
    case "all":
    default:
      return undefined;
  }
};

export const EpicsDashboard = () => {
  useComponentTracking("EpicsDashboard");
  useTimeTracking("EpicsDashboard");

  const [range, setRange] = useState<RangeOption>("90");
  const [organizerSearch, setOrganizerSearch] = useState("");
  const [selectedEpics, setSelectedEpics] = useState<string[]>([]);
  const [data, setData] = useState<EpicRun[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEpicRuns = async () => {
      setLoading(true);
      try {
        const params: Record<string, string> = {};
        const startDate = rangeToStartDate(range);
        if (startDate) params.startDate = startDate;

        const res = await axios.get<EpicRun[]>(
          `${process.env.REACT_APP_API}public/epic-runs`,
          { params }
        );
        setData(res.data);
      } catch (err) {
        console.error("Failed to fetch epic runs", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEpicRuns();
  }, [range]);

  const toggleEpic = (name: string) => {
    setSelectedEpics((prev) => {
      const next = prev.includes(name)
        ? prev.filter((e) => e !== name)
        : [...prev, name];
      if (next.length) {
        analytics.trackSearch(next.join(","), "epic_run_epic_name");
      }
      return next;
    });
  };

  const epicOptions = useMemo(() => {
    const names = new Set(
      data.map((run) => run.epicName).filter((name): name is string => !!name)
    );
    return Array.from(names).sort();
  }, [data]);

  const filteredData = useMemo(() => {
    const organizerQuery = organizerSearch.toLowerCase();
    return data.filter((run) => {
      const organizerMatch = run.organizer
        ? run.organizer.toLowerCase().includes(organizerQuery)
        : organizerQuery === "";
      const epicMatch =
        selectedEpics.length === 0 ||
        (run.epicName ? selectedEpics.includes(run.epicName) : false);
      return organizerMatch && epicMatch;
    });
  }, [data, organizerSearch, selectedEpics]);

  const countsByHour = useMemo(() => {
    const counts = new Array(24).fill(0);
    filteredData.forEach((run) => {
      const hour = new Date(run.runDate).getHours();
      counts[hour] += 1;
    });
    return counts;
  }, [filteredData]);

  const stats = useMemo(() => {
    const organizers = new Set(
      filteredData.filter((r) => r.organizer).map((r) => r.organizer)
    );
    const busiestHour = countsByHour.reduce(
      (best, count, hour) => (count > best.count ? { hour, count } : best),
      { hour: 0, count: 0 }
    );
    return {
      totalRuns: filteredData.length,
      uniqueOrganizers: organizers.size,
      busiestHour: busiestHour.count > 0 ? busiestHour.hour : null
    };
  }, [filteredData, countsByHour]);

  return (
    <Container className="py-4">
      <Title text="Epics Dashboard" />

      <Form onSubmit={(e) => e.preventDefault()}>
        <Row className="align-items-end mb-3 text-light">
          <Col xs={12} md={5}>
            <Form.Group>
              <Form.Label>Organizer</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search organizer"
                value={organizerSearch}
                onChange={(e) => {
                  setOrganizerSearch(e.target.value);
                  if (e.target.value) {
                    analytics.trackSearch(e.target.value, "epic_run_organizer");
                  }
                }}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label>Date range</Form.Label>
              <Form.Select
                value={range}
                onChange={(e) => setRange(e.target.value as RangeOption)}
              >
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="year">This year</option>
                <option value="all">All time</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3 text-light">
          <Col>
            <div className="d-flex align-items-center flex-wrap gap-2">
              <span className="me-1">Epic:</span>
              {epicOptions.map((name) => {
                const color = getEpicColor(name);
                const selected = selectedEpics.includes(name);
                return (
                  <Button
                    key={name}
                    size="sm"
                    className="rounded-pill d-inline-flex align-items-center gap-2"
                    style={{
                      borderColor: color,
                      backgroundColor: selected ? color : "transparent",
                      color: selected ? "#161523" : "#f1f4c6"
                    }}
                    onClick={() => toggleEpic(name)}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: selected ? "#161523" : color
                      }}
                    />
                    {name}
                  </Button>
                );
              })}
              {selectedEpics.length > 0 && (
                <Button
                  size="sm"
                  variant="link"
                  className="text-light"
                  onClick={() => setSelectedEpics([])}
                >
                  Clear
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Form>

      {loading ? (
        <div className="text-center my-4">
          <Spinner animation="border" variant="light" />
        </div>
      ) : (
        <>
          <Row className="mb-4 text-light text-center">
            <Col xs={4}>
              <div className="fs-3 fw-bold">{stats.totalRuns}</div>
              <div className="text-muted">Runs</div>
            </Col>
            <Col xs={4}>
              <div className="fs-3 fw-bold">{stats.uniqueOrganizers}</div>
              <div className="text-muted">Organizers</div>
            </Col>
            <Col xs={4}>
              <div className="fs-3 fw-bold">
                {stats.busiestHour === null ? "-" : `${stats.busiestHour}:00`}
              </div>
              <div className="text-muted">Busiest Hour</div>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col>
              <EpicRunsByHourChart countsByHour={countsByHour} />
            </Col>
          </Row>

          <EpicRunsTable data={filteredData} />
        </>
      )}
    </Container>
  );
};

export default EpicsDashboard;
